/* Platform Admin — Review Dashboard voor alle tenants.
 * Alleen zichtbaar voor rol platform_admin.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Building2, Plus, Check, X, RotateCcw, Ban, Play, FlaskConical, Eye } from "lucide-react";

const STATUS_META = {
  uitgenodigd: { label: "Uitgenodigd", cls: "bg-neutral-100 text-neutral-700" },
  concept: { label: "Concept", cls: "bg-slate-100 text-slate-700" },
  in_behandeling: { label: "In behandeling", cls: "bg-sky-100 text-sky-700" },
  ingediend: { label: "Ingediend — wacht op review", cls: "bg-amber-100 text-amber-800" },
  controle_nodig: { label: "Controle nodig", cls: "bg-orange-100 text-orange-800" },
  goedgekeurd: { label: "Goedgekeurd", cls: "bg-emerald-100 text-emerald-700" },
  actief: { label: "Actief", cls: "bg-green-100 text-green-800" },
  geblokkeerd: { label: "Geblokkeerd", cls: "bg-red-100 text-red-700" },
  afgewezen: { label: "Afgewezen", cls: "bg-red-50 text-red-600" },
  geannuleerd: { label: "Geannuleerd", cls: "bg-neutral-200 text-neutral-600" },
};

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [reviewOrg, setReviewOrg] = useState(null); // {org, action, notes}
  const [progressMap, setProgressMap] = useState({}); // org_id → session

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/organizations");
      const items = r.data.items || [];
      setOrgs(items);
      // Load onboarding sessions for progress badges (best-effort)
      const map = {};
      await Promise.all(items.map(async (o) => {
        try {
          const s = await api.get(`/organizations/${o.id}/onboarding`);
          map[o.id] = s.data;
        } catch { /* ignore */ }
      }));
      setProgressMap(map);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return orgs.filter(o => {
      if (filter === "waiting" && o.status !== "ingediend") return false;
      if (filter === "active" && o.status !== "actief") return false;
      if (filter === "blocked" && !["geblokkeerd", "afgewezen"].includes(o.status)) return false;
      if (q && !o.name.toLowerCase().includes(q.toLowerCase()) && !(o.slug || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [orgs, filter, q]);

  const stats = useMemo(() => {
    const s = { total: orgs.length, waiting: 0, active: 0, blocked: 0 };
    orgs.forEach(o => {
      if (o.status === "ingediend") s.waiting++;
      if (o.status === "actief") s.active++;
      if (["geblokkeerd", "afgewezen"].includes(o.status)) s.blocked++;
    });
    return s;
  }, [orgs]);

  const isPlatformSuper = user?.role === "platform_admin" ||
    (user?.role === "superadmin" && !user?.organization_id);
  if (!isPlatformSuper) {
    return (
      <div className="p-8 text-center">
        <div className="text-lg font-medium text-neutral-800 mb-2">Toegang beperkt</div>
        <div className="text-sm text-neutral-500">Alleen platformbeheerders hebben toegang tot dit dashboard.</div>
      </div>
    );
  }

  const doReview = async (org, action, notes = "") => {
    try {
      await api.post(`/organizations/${org.id}/onboarding/review`, { action, notes });
      toast.success(`${STATUS_META[actionToStatus(action)]?.label || action} — ${org.name}`);
      await load();
      setReviewOrg(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activate = async (org) => {
    if (!confirm(`Activeer ${org.name} voor productie-gebruik?`)) return;
    try {
      await api.post(`/organizations/${org.id}/onboarding/activate`);
      toast.success(`${org.name} is nu actief`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const seedTemplates = async (org) => {
    if (!confirm(`Standaard sjablonen (contractpakketten, beleidsdocumenten) toevoegen aan ${org.name}?`)) return;
    try {
      const r = await api.post(`/organizations/${org.id}/seed-templates`);
      toast.success(`Sjablonen geladen: ${r.data.contract_packages} pakketten · ${r.data.policy_documents} documenten`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="platform-admin-page">
      <PageHeader title="Platform-beheer" subtitle="Overzicht van alle tenants en onboarding-status" testid="platform-header" />

      {/* KPI-tegels */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI title="Totaal tenants" value={stats.total} icon={Building2} color="sky" testid="kpi-total" />
        <KPI title="Wacht op review" value={stats.waiting} icon={Eye} color="amber" testid="kpi-waiting" />
        <KPI title="Actief" value={stats.active} icon={Check} color="emerald" testid="kpi-active" />
        <KPI title="Geblokkeerd/Afgewezen" value={stats.blocked} icon={Ban} color="red" testid="kpi-blocked" />
      </div>

      {/* Filters */}
      <Card className="p-3 mb-3 bg-white shadow-none">
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Zoek tenant of slug…" value={q} onChange={e => setQ(e.target.value)} className="max-w-xs" data-testid="pa-search" />
          <div className="flex gap-1 flex-wrap">
            {[["all", "Alle"], ["waiting", "Op review"], ["active", "Actief"], ["blocked", "Geblokkeerd"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`pa-filter-${k}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium ${filter === k ? "bg-[color:var(--sage)] text-white" : "bg-neutral-100 text-neutral-700"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="ml-auto flex gap-2">
            <Button onClick={() => setShowNew(true)} style={{ background: "var(--sage)" }} className="text-white" data-testid="pa-new-btn">
              <Plus className="h-4 w-4 mr-1" /> Nieuwe tenant
            </Button>
          </div>
        </div>
      </Card>

      {/* Tenant lijst */}
      {loading ? (
        <div className="text-center py-10 text-sm text-neutral-500">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-neutral-500" data-testid="pa-empty">Geen tenants gevonden.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(o => {
            const meta = STATUS_META[o.status] || STATUS_META.concept;
            const s = progressMap[o.id];
            const prog = s?.progress?.percent ?? null;
            return (
              <Card key={o.id} className="p-4 bg-white shadow-none" data-testid={`pa-tenant-${o.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-neutral-900">{o.name}</div>
                      <Badge className={`${meta.cls} border-transparent text-[10px]`} data-testid={`pa-status-${o.id}`}>{meta.label}</Badge>
                      {o.test_mode && <Badge variant="outline" className="text-[10px]">Testmodus</Badge>}
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      slug: {o.slug} · KVK: {o.kvk || "—"} · {o.email || "—"}
                    </div>
                    {prog !== null && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 flex-1 max-w-xs bg-neutral-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-sky-500 to-indigo-500" style={{ width: `${prog}%` }} />
                        </div>
                        <div className="text-[10px] text-neutral-500">{prog}% ({s?.progress?.done}/{s?.progress?.total_required})</div>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-start">
                    {o.status === "ingediend" && (
                      <>
                        <Button size="sm" onClick={() => doReview(o, "goedkeuren")} style={{ background: "var(--sage)" }} className="text-white h-8" data-testid={`pa-approve-${o.id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Goedkeuren
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewOrg({ org: o, action: "terugsturen" })} className="h-8" data-testid={`pa-return-${o.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Terugsturen
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewOrg({ org: o, action: "test_mode" })} className="h-8 border-sky-300 text-sky-700" data-testid={`pa-test-${o.id}`}>
                          <FlaskConical className="h-3.5 w-3.5 mr-1" /> Testmodus
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setReviewOrg({ org: o, action: "afwijzen" })} className="h-8 border-red-300 text-red-700" data-testid={`pa-reject-${o.id}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Afwijzen
                        </Button>
                      </>
                    )}
                    {o.status === "goedgekeurd" && (
                      <Button size="sm" onClick={() => activate(o)} style={{ background: "var(--sage)" }} className="text-white h-8" data-testid={`pa-activate-${o.id}`}>
                        <Play className="h-3.5 w-3.5 mr-1" /> Activeer
                      </Button>
                    )}
                    {["actief", "concept", "controle_nodig"].includes(o.status) && (
                      <Button size="sm" variant="outline" onClick={() => setReviewOrg({ org: o, action: "blokkeren" })} className="h-8 border-red-300 text-red-700" data-testid={`pa-block-${o.id}`}>
                        <Ban className="h-3.5 w-3.5 mr-1" /> Blokkeren
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => seedTemplates(o)} className="h-8" title="Standaard sjablonen laden" data-testid={`pa-seed-${o.id}`}>
                      <FlaskConical className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => window.open(`/platform/organisaties/${o.id}`, "_blank")} className="h-8" data-testid={`pa-view-${o.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {o.review_notes && (
                  <div className="mt-2 text-xs bg-amber-50 border border-amber-200 p-2 rounded text-amber-800">
                    <strong>Beoordelingsnotitie:</strong> {o.review_notes}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Nieuwe tenant dialog */}
      <NewTenantDialog open={showNew} onClose={() => setShowNew(false)} onCreated={load} />

      {/* Review met notes dialog */}
      {reviewOrg && (
        <ReviewNotesDialog
          org={reviewOrg.org}
          action={reviewOrg.action}
          onClose={() => setReviewOrg(null)}
          onSubmit={(notes) => doReview(reviewOrg.org, reviewOrg.action, notes)}
        />
      )}
    </div>
  );
}

function actionToStatus(action) {
  const m = { goedkeuren: "goedgekeurd", afwijzen: "afgewezen", terugsturen: "controle_nodig", blokkeren: "geblokkeerd", test_mode: "goedgekeurd" };
  return m[action] || action;
}

function KPI({ title, value, icon: Ic, color, testid }) {
  const cls = {
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
  }[color] || "bg-neutral-50 text-neutral-700";
  return (
    <Card className={`p-3 shadow-none border ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Ic className="h-5 w-5" />
        <div className="min-w-0">
          <div className="text-xs opacity-80">{title}</div>
          <div className="text-2xl font-semibold">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function NewTenantDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", slug: "", email: "", kvk: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Naam verplicht");
    setBusy(true);
    try {
      await api.post("/organizations", f);
      toast.success(`Tenant "${f.name}" aangemaakt`);
      setF({ name: "", slug: "", email: "", kvk: "" });
      onClose();
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="pa-new-dialog">
        <DialogHeader>
          <DialogTitle>Nieuwe tenant aanmaken</DialogTitle>
          <DialogDescription>Maak een lege organisatie aan; de KDV Super Admin voltooit de onboarding.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Naam *</Label>
            <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Kinderopvang Rotterdam" data-testid="pa-new-name" />
          </div>
          <div>
            <Label className="text-xs">Slug (URL-vriendelijk)</Label>
            <Input value={f.slug} onChange={e => setF({ ...f, slug: e.target.value })} placeholder="kdv-rotterdam" data-testid="pa-new-slug" />
          </div>
          <div>
            <Label className="text-xs">E-mail contact</Label>
            <Input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="info@kdv-rotterdam.nl" data-testid="pa-new-email" />
          </div>
          <div>
            <Label className="text-xs">KVK-nummer (8 cijfers)</Label>
            <Input value={f.kvk} onChange={e => setF({ ...f, kvk: e.target.value })} placeholder="12345678" data-testid="pa-new-kvk" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button onClick={submit} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="pa-new-submit">
            {busy ? "Bezig…" : "Aanmaken"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewNotesDialog({ org, action, onClose, onSubmit }) {
  const [notes, setNotes] = useState("");
  const labels = { terugsturen: "Terugsturen naar KDV", afwijzen: "Afwijzen", blokkeren: "Blokkeren", test_mode: "In testmodus zetten" };
  const required = ["terugsturen", "afwijzen", "blokkeren"].includes(action);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="pa-review-dialog">
        <DialogHeader>
          <DialogTitle>{labels[action] || action} — {org.name}</DialogTitle>
          <DialogDescription>
            {required ? "Toelichting verplicht — de KDV ziet deze notitie." : "Optionele toelichting."}
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Wat moet er anders?" data-testid="pa-review-notes" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuleren</Button>
          <Button
            onClick={() => onSubmit(notes)}
            disabled={required && !notes.trim()}
            style={{ background: "var(--sage)" }} className="text-white" data-testid="pa-review-submit"
          >
            Bevestigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
